/**
 * Hook for generating menu items with badges
 */

import { ReloadOutlined } from "@ant-design/icons";
import { Button, type MenuProps, Space, Typography } from "antd";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { retrySelectedRolePermissions } from "@/contexts/auth/services/rolePermissionService";
import { isPermissionCatalogueV2 } from "@/env-variables";
import { addBadgeToMenuLabel } from "@/helper/menuBadgeHelper";
import { useTsTmcProviderRestrictionPolicy } from "@/hooks/useTsTmcProviderRestriction";
import { ERoutesPathname } from "@/routers/config";
import { getMenuItems } from "@/routers/navigation-adapters";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo";
import { useDeliveryOrdersTotal } from "@/stores/domains/inventory";
import { useRolePermissions, useRolePermissionsStatus } from "@/stores/domains/user/userStore";

const PERMISSIONS_ERROR_KEY = "__role-permissions-error__";

/**
 * Menu entry shown when the selected role's permissions could not be loaded.
 * Without it the sidebar is simply empty and the failure looks like "you have no access".
 */
const permissionsErrorItem = (): AntdLikeMenuItem => ({
  key: PERMISSIONS_ERROR_KEY,
  label: (
    <Space direction="vertical" size={4} style={{ padding: "8px 4px" }}>
      <Typography.Text type="danger" style={{ whiteSpace: "normal" }}>
        Could not load your permissions.
      </Typography.Text>
      <Button
        size="small"
        icon={<ReloadOutlined />}
        onClick={() => {
          void retrySelectedRolePermissions();
        }}
      >
        Retry
      </Button>
    </Space>
  ),
});

// Convert to Antd Menu format with badges
type AntdLikeMenuItem = {
  key: string;
  icon?: ReactNode;
  label: ReactNode;
  children?: AntdLikeMenuItem[];
};

export const useMenuWithBadges = (): MenuProps["items"] => {
  const deliveryOrdersTotal = useDeliveryOrdersTotal();
  const role = useAdditionalInfo((state) => state.role);
  const policy = useTsTmcProviderRestrictionPolicy();
  // Under the v2 catalogue the sidebar is filtered by the selected role's permissions, which
  // arrive after the first render; recompute the menu once they land.
  const rolePermissions = useRolePermissions();
  const rolePermissionsStatus = useRolePermissionsStatus();

  const menuItems = useMemo(() => {
    if (!role) return [];

    if (isPermissionCatalogueV2()) {
      // A failed load is not "no access": say so and offer a retry.
      if (rolePermissionsStatus === "error") {
        return [permissionsErrorItem()] as unknown as MenuProps["items"];
      }
      // Nothing is visible until the permissions arrive; building the menu now would cache an
      // empty sidebar, so wait and let the load re-run this memo.
      if (rolePermissions.length === 0) return [];
    }

    const items = getMenuItems();
    const visibleItems = policy.hidePatientList
      ? items.filter((item) => item.url !== ERoutesPathname.patient)
      : items;

    // Badge data mapping
    const badgeData: Record<string, number> = {
      "/inventory/delivery": deliveryOrdersTotal,
    };

    const convertToAntdFormat = (menuItems: typeof items): MenuProps["items"] => {
      const toAntdLike = (nodes: typeof items): AntdLikeMenuItem[] => {
        return nodes.map((item) => {
          const count = badgeData[item.url];
          const label = addBadgeToMenuLabel(item.title, item.url, count);

          const menuItem: AntdLikeMenuItem = {
            key: item.url,
            icon: item.icon,
            label,
          };

          if (item.children && item.children.length > 0) {
            menuItem.children = item.children.map((child) => {
              const childCount = badgeData[child.url];
              const childLabel = addBadgeToMenuLabel(child.title, child.url, childCount);

              const childItem: AntdLikeMenuItem = {
                key: child.url,
                label: childLabel,
              };

              if (child.children && child.children.length > 0) {
                childItem.children = child.children.map((grandChild) => {
                  const grandChildCount = badgeData[grandChild.url];
                  const grandChildLabel = addBadgeToMenuLabel(
                    grandChild.title,
                    grandChild.url,
                    grandChildCount,
                  );

                  return {
                    key: grandChild.url,
                    label: grandChildLabel,
                  };
                });
              }

              return childItem;
            });
          }

          return menuItem;
        });
      };

      return toAntdLike(menuItems) as unknown as MenuProps["items"];
    };

    return convertToAntdFormat(visibleItems);
  }, [deliveryOrdersTotal, role, policy.hidePatientList, rolePermissions, rolePermissionsStatus]);

  return menuItems;
};
